// @ts-check
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import ActivationLabelExport from '../../ActivationLabelExport';
import ExtensionModal from '../../LicenseExtensionModal';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { useToast } from '../../components/Toast';
import { BindDeviceModal } from '../BindDeviceModal';
import { ConfirmModal } from '../ConfirmModal';
import { useDebounce } from '../../hooks/useDebounce';
import { COLUMNS, SORTABLE_KEYS, isValidForQRCode, useColumnVisibility } from './columns';
import { EVICTION_GRANT_COPY } from './evictionGrantCopy';
import { ActionMenu } from './ActionMenu';
import { useLicenseFetch } from './hooks/useLicenseFetch';
import { useSelection } from './hooks/useSelection';
import { useOptimisticLicense } from './hooks/useOptimisticLicense';
import { LABEL_REISSUE_COPY } from './labelReissueCopy';
import '../../forms.css';
import '../../LicenseTable.css';

const DEFAULT_SORT = { key: null, dir: null }; // null → createdAt desc

function readSortFromUrl() {
  const raw = new URLSearchParams(window.location.search).get('sort');
  if (raw && raw.includes(':')) {
    const [key, dir] = raw.split(':');
    if (SORTABLE_KEYS[key] && (dir === 'asc' || dir === 'desc')) {
      return { key, dir };
    }
  }
  return DEFAULT_SORT;
}

function sortLicenses(list, sort) {
  const key = sort.key || 'createdAt';
  const dir = sort.key ? sort.dir : 'desc';
  const field = SORTABLE_KEYS[key] || 'createdAt';
  const sorted = [...list].sort((a, b) => (a[field] || '').localeCompare(b[field] || ''));
  return dir === 'desc' ? sorted.reverse() : sorted;
}

function LicenseTable() {
  const auth = useAuth();
  const { addToast } = useToast();

  // --- Data + derived state ---
  const {
    licenses,
    setLicenses,
    nextToken,
    loading,
    setLoading,
    error,
    setError,
    fetchLicenses,
    fetchLicenseExpiry,
  } = useLicenseFetch(auth);

  // --- Filters ---
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [customerNameFilter, setCustomerNameFilter] = useState('');
  const [customerNumberFilter, setCustomerNumberFilter] = useState('');
  const [expiryStatusFilter, setExpiryStatusFilter] = useState('all');
  const [sort, setSort] = useState(readSortFromUrl);

  // Debounce all text filters with the same hook for consistent behavior (B3).
  const debouncedCustomerNameFilter = useDebounce(customerNameFilter, 300);
  const debouncedCustomerNumberFilter = useDebounce(customerNumberFilter, 300);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { isHidden, toggle: toggleColumn } = useColumnVisibility();

  // --- Modals not owned by the optimistic hook ---
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);

  // Initial load + refresh whenever a server-side filter changes.
  useEffect(() => {
    fetchLicenses(debouncedSearchTerm, pageSize, includeDeleted, null, true, expiryStatusFilter, debouncedCustomerNumberFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchLicenses, debouncedSearchTerm, pageSize, includeDeleted, expiryStatusFilter, debouncedCustomerNumberFilter]);

  const refetch = useCallback(
    () => fetchLicenses(debouncedSearchTerm, pageSize, includeDeleted, null, true, expiryStatusFilter, debouncedCustomerNumberFilter),
    [fetchLicenses, debouncedSearchTerm, pageSize, includeDeleted, expiryStatusFilter, debouncedCustomerNumberFilter],
  );

  // Sort (client-side over loaded rows) then apply client-side customer filters.
  const visibleColumns = COLUMNS.filter((col) => !isHidden(col.key));
  const sortedLicenses = sortLicenses(licenses, sort);
  const filteredLicenses = sortedLicenses.filter((license) => {
    const matchesCustomerName =
      !debouncedCustomerNameFilter ||
      (license.customerName &&
        license.customerName.toLowerCase().includes(debouncedCustomerNameFilter.toLowerCase()));
    const matchesCustomerNumber =
      !debouncedCustomerNumberFilter ||
      (license.customerNumber &&
        license.customerNumber.toLowerCase().includes(debouncedCustomerNumberFilter.toLowerCase()));
    return matchesCustomerName && matchesCustomerNumber;
  });

  const selection = useSelection(filteredLicenses);
  const {
    selectedLicenses,
    setSelectedLicenses,
    handleLicenseSelect,
    handleSelectAll,
    handleSelectAllMatching,
    getSelectedLicenses,
    selectedCount,
  } = selection;

  const actions = useOptimisticLicense({
    auth,
    licenses,
    setLicenses,
    setError,
    setLoading,
    refetch,
    addToast,
    setSelectedLicenses,
    includeDeleted,
  });

  // --- Extension modal (needs an expiry fetch before opening) ---
  const handleOpenExtensionModal = async (license) => {
    setLoading(true);
    try {
      const expiryInfo = await fetchLicenseExpiry(license.serialNumber);
      if (expiryInfo) {
        setSelectedLicense({
          ...license,
          expiresAt: expiryInfo.expiresAt,
          daysRemaining: expiryInfo.daysRemaining,
          extensionHistory: expiryInfo.extensionHistory,
          isExpired: expiryInfo.isExpired,
        });
        setShowExtensionModal(true);
      }
    } catch (err) {
      console.error('Error preparing license extension:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExtensionComplete = (result) => {
    setLicenses((prev) =>
      prev.map((lic) =>
        lic.serialNumber === result.serialNumber ? { ...lic, expiresAt: result.newExpiryDate } : lic,
      ),
    );
  };

  // --- Filter handlers ---
  const loadMore = () => {
    if (nextToken && !loading) {
      fetchLicenses(debouncedSearchTerm, pageSize, includeDeleted, nextToken, false, expiryStatusFilter, debouncedCustomerNumberFilter);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCustomerNameFilter('');
    setCustomerNumberFilter('');
    setExpiryStatusFilter('all');
  };

  const hasActiveFilters = Boolean(
    searchTerm || customerNameFilter || customerNumberFilter || expiryStatusFilter !== 'all',
  );

  // --- Sorting → URL (B4) ---
  const applySortToUrl = (next) => {
    const params = new URLSearchParams(window.location.search);
    if (next.key) {
      params.set('sort', `${next.key}:${next.dir}`);
    } else {
      params.delete('sort');
    }
    const qs = params.toString();
    const url = qs ? `?${qs}${window.location.hash}` : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState(null, '', url);
  };

  const toggleSort = (key) => {
    setSort((prev) => {
      let next;
      if (prev.key !== key) next = { key, dir: 'asc' };
      else if (prev.dir === 'asc') next = { key, dir: 'desc' };
      else next = DEFAULT_SORT;
      applySortToUrl(next);
      return next;
    });
  };

  const ariaSortFor = (key) => {
    if (sort.key !== key) return 'none';
    return sort.dir === 'asc' ? 'ascending' : 'descending';
  };

  // --- Rendering ---
  const highlightMatch = (text) => {
    if (!searchTerm.trim() || !text) return text;
    const lowerText = text.toLowerCase();
    const startIndex = lowerText.indexOf(searchTerm.toLowerCase());
    if (startIndex === -1) return text;
    const endIndex = startIndex + searchTerm.length;
    return (
      <span>
        {text.substring(0, startIndex)}
        <mark className="search-highlight">{text.substring(startIndex, endIndex)}</mark>
        {text.substring(endIndex)}
      </span>
    );
  };
  const columnCtx = { highlightMatch };

  // Header (select) + visible data columns + optional Deleted At + Status + Actions
  const totalColumnCount = 1 + visibleColumns.length + (includeDeleted ? 1 : 0) + 2;

  return (
    <div className="license-table-root">
      <h2>License Management</h2>

      {error && (
        <div role="alert" className="license-error">
          <span>Error: {error}</span>
          <button
            type="button"
            className="license-error__dismiss"
            aria-label="Dismiss error"
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Search and filters */}
      <div className="filter-stack">
        <div className="filter-row">
          <div className="filter-field">
            <label htmlFor="search">Search:</label>
            <input
              id="search"
              type="text"
              className="form-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="By serial number"
              aria-label="Search licenses by serial number"
              disabled={loading}
            />
          </div>
          <div className="filter-field">
            <label htmlFor="pageSize">Show:</label>
            <select
              id="pageSize"
              className="form-select"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              aria-label="Select number of items per page"
              disabled={loading}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="filter-field">
            <input
              type="checkbox"
              id="includeDeleted"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              disabled={loading}
            />
            <label htmlFor="includeDeleted">Include Deleted</label>
          </div>
          <div className="filter-field">
            <label htmlFor="expiryStatus">Expiry Status:</label>
            <select
              id="expiryStatus"
              className="form-select"
              value={expiryStatusFilter}
              onChange={(e) => setExpiryStatusFilter(e.target.value)}
              aria-label="Filter by expiry status"
              disabled={loading}
            >
              <option value="all">All</option>
              <option value="valid">Valid</option>
              <option value="expired">Expired</option>
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-field">
            <label htmlFor="customerName">Customer Name:</label>
            <input
              id="customerName"
              type="text"
              className="form-input"
              value={customerNameFilter}
              onChange={(e) => setCustomerNameFilter(e.target.value)}
              placeholder="Filter by customer name"
              aria-label="Filter licenses by customer name"
              disabled={loading}
            />
          </div>
          <div className="filter-field">
            <label htmlFor="customerNumber">Customer Number:</label>
            <input
              id="customerNumber"
              type="text"
              className="form-input"
              value={customerNumberFilter}
              onChange={(e) => setCustomerNumberFilter(e.target.value)}
              placeholder="Filter by customer number"
              aria-label="Filter licenses by customer number"
              disabled={loading}
            />
          </div>
          <div className="filter-field">
            <Button size="sm" variant="ghost" onClick={clearFilters} disabled={loading}>
              Clear Filters
            </Button>
          </div>

          {/* Column visibility (D3 — persisted to localStorage) */}
          <details className="column-toggle">
            <summary className="column-toggle__summary">Columns</summary>
            <div className="column-toggle__menu">
              {COLUMNS.map((col) => (
                <label key={col.key} className="column-toggle__item">
                  <input
                    type="checkbox"
                    checked={!isHidden(col.key)}
                    onChange={() => toggleColumn(col.key)}
                  />
                  {col.label}
                </label>
              ))}
            </div>
          </details>
        </div>
      </div>

      {/* Toolbar */}
      <div className="qr-toolbar">
        <Button variant="ghost" onClick={() => setShowActivationModal(true)} disabled={selectedCount === 0}>
          Activation Labels
        </Button>
        <Button variant="ghost" onClick={handleSelectAllMatching} disabled={loading}>
          Select All Matching Filter
        </Button>
      </div>

      {/* Licenses table */}
      <div role="region" aria-label="Licenses table" className="license-table-wrap">
        <table className="license-table">
          <thead>
            <tr>
              <th scope="col" className="license-table__select-col">
                <input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  aria-label="Select all valid licenses"
                />
              </th>
              {visibleColumns.map((col) => {
                const sortable = Boolean(SORTABLE_KEYS[col.key]);
                return (
                  <th key={col.key} scope="col" aria-sort={sortable ? ariaSortFor(col.key) : undefined}>
                    {sortable ? (
                      <button
                        type="button"
                        className="license-table__sort"
                        onClick={() => toggleSort(col.key)}
                      >
                        {col.label}
                        <span aria-hidden="true" className="license-table__sort-icon">
                          {sort.key === col.key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                        </span>
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
              {includeDeleted && <th scope="col">Deleted At</th>}
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLicenses.length > 0 ? (
              filteredLicenses.map((lic) => {
                const inline = lic.deleted
                  ? { label: 'Restore', onClick: () => actions.handleRestore(lic.serialNumber), variant: 'ghost' }
                  : lic.active
                    ? { label: 'Disable', onClick: () => actions.handleDisable(lic.serialNumber), variant: 'ghost' }
                    : { label: 'Enable', onClick: () => actions.handleEnable(lic.serialNumber), variant: 'primary' };

                const items = lic.deleted
                  ? []
                  : [
                      // Ruled 2026-09-29: a registered licence offers only Unregister; an
                      // unregistered one offers Bind (pre-provisioning) or Unbind (remove a pre-bind).
                      lic.registered
                        ? {
                            key: 'unregister',
                            label: 'Unregister device',
                            onClick: () => actions.handleUnregister(lic.serialNumber),
                            title: 'Clear this license registration and its PoP enrolment so a replacement tablet can register',
                          }
                        : lic.deviceId
                          ? { key: 'unbind', label: 'Unbind device', onClick: () => actions.handleUnbind(lic.serialNumber) }
                          : { key: 'bind', label: 'Bind device', onClick: () => actions.handleBind(lic.serialNumber, null) },
                      {
                        key: 'extend',
                        label: 'Extend',
                        onClick: () => handleOpenExtensionModal(lic),
                        title: 'Extend or update license expiry',
                      },
                      {
                        key: 'regen',
                        label: 'Regenerate key',
                        onClick: () => actions.handleRegenerateKey(lic.serialNumber),
                        title: lic.registrationCodeUsed
                          ? 'Regenerate key - QR code has been used'
                          : 'Regenerate license key',
                      },
                      {
                        key: 'eviction-grant',
                        label: lic.evictionGrant === true
                          ? EVICTION_GRANT_COPY.disableMenuLabel
                          : EVICTION_GRANT_COPY.enableMenuLabel,
                        onClick: () => actions.handleSetEvictionGrant(lic.serialNumber, lic.evictionGrant !== true),
                        title: EVICTION_GRANT_COPY.actionTitle,
                      },
                      { key: 'delete', label: 'Soft delete', onClick: () => actions.handleDelete(lic.serialNumber) },

                    ];

                return (
                  <tr key={lic.serialNumber} className={lic.deleted ? 'is-deleted' : ''}>
                    <td className="license-table__select-cell">
                      <input
                        type="checkbox"
                        checked={!!selectedLicenses[lic.serialNumber]}
                        onChange={(e) => handleLicenseSelect(lic.serialNumber, e.target.checked)}
                        aria-label={`Select license ${lic.serialNumber}`}
                        disabled={!isValidForQRCode(lic)}
                        title={!isValidForQRCode(lic) ? 'License must be active and not yet registered' : ''}
                      />
                    </td>
                    {visibleColumns.map((col) => (
                      <td key={col.key}>{col.render(lic, columnCtx)}</td>
                    ))}
                    {includeDeleted && (
                      <td>{lic.deletedAt ? new Date(lic.deletedAt).toLocaleString() : '-'}</td>
                    )}
                    <td>
                      <Badge variant={lic.deleted ? 'deleted' : lic.active ? 'active' : 'inactive'}>
                        {lic.deleted ? 'Deleted' : lic.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td>
                      <ActionMenu
                        inline={inline}
                        items={items}
                        loading={loading}
                        label={`Actions for ${lic.serialNumber}`}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={totalColumnCount} className="license-table__empty">
                  {!loading &&
                    (hasActiveFilters ? (
                      <div className="license-empty">
                        <p>No licenses match your filters.</p>
                        <Button size="sm" variant="ghost" onClick={clearFilters}>
                          Clear filters
                        </Button>
                      </div>
                    ) : (
                      <p>No licenses created yet.</p>
                    ))}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {nextToken && (
        <Button variant="ghost" onClick={loadMore} disabled={loading} loading={loading}>
          Load More
        </Button>
      )}

      <div aria-live="polite" className="license-status">
        {loading && <p>Loading data...</p>}
        {!loading && <p>Showing {licenses.length} licenses.</p>}
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={actions.modalState.isOpen}
        title={actions.modalState.title}
        message={actions.modalState.message}
        variant={actions.modalState.variant}
        confirmText={actions.modalState.confirmText}
        confirmLabel={actions.modalState.confirmLabel}
        submitting={actions.modalState.submitting}
        onConfirm={() => actions.modalState.action && actions.modalState.action()}
        onCancel={actions.closeConfirmModal}
      />

      {/* Bind / Rebind Modal */}
      <BindDeviceModal
        isOpen={actions.bindModalState.isOpen}
        serialNumber={actions.bindModalState.serialNumber}
        currentDeviceId={actions.bindModalState.currentDeviceId}
        auth={auth}
        onBind={actions.onBindSubmit}
        onClose={actions.closeBindModal}
      />

      {/* Activation Label Export Modal */}
      {showActivationModal && (
        <ActivationLabelExport
          licenses={getSelectedLicenses().map(({ licenseKey, licenseKeyHash, ...safe }) => safe)}
          onClose={() => { setShowActivationModal(false); refetch(); }}
        />
      )}

      {/* Replacement label after unregister / regenerate-key (the old label is revoked) */}
      {actions.labelReissue && (
        <ActivationLabelExport
          licenses={[
            licenses.find((lic) => lic.serialNumber === actions.labelReissue.serialNumber)
              || { serialNumber: actions.labelReissue.serialNumber },
          ].map(({ licenseKey, licenseKeyHash, ...safe }) => safe)}
          autoGenerate
          notice={actions.labelReissue.notice}
          failureNotice={LABEL_REISSUE_COPY.failureNotice}
          onClose={actions.closeLabelReissue}
        />
      )}

      {/* License Extension Modal */}
      {showExtensionModal && (
        <ExtensionModal
          license={selectedLicense}
          onClose={() => {
            setShowExtensionModal(false);
            setSelectedLicense(null);
          }}
          onExtend={handleExtensionComplete}
        />
      )}
    </div>
  );
}

export default LicenseTable;
